import type { AxiosError } from 'axios'
import type { HttpClient } from '@/infrastructure/http/HttpClient'
import type { AdvanceRenditionRepository } from '@/domain/advanceRendition/AdvanceRenditionRepository'
import type {
  AdvanceEditRole,
  AdvanceReceiptDecision,
  AdvanceReceiptsDetail,
  RequestActionOutcome,
  ReviewDocumentsInput,
  ReviewDocumentsResult,
} from '@/domain/advanceRendition/AdvanceRendition'
import type { RenditionInvoice } from '@/domain/rendition/Rendition'
import type { RenditionFormData } from '@/domain/rendition/RenditionRepository'
import { hydrateRenditionInvoices, mapInvoice, type RawInvoiceItem } from '@/infrastructure/rendition/renditionPayload'

/**
 * Employee-advance receipts (finnecto-api `RequestAdvanceReceiptsView`). The read ships each receipt in the
 * rendition item shape, so the rendition carousel renders it as is; edits reuse the rendition actions.
 */

export interface RawAdvanceReceiptItem extends RawInvoiceItem {
  rendered_amount: number
  review_status: AdvanceReceiptDecision
}

export interface RawAdvanceReceiptsDetail {
  id: number
  policy: { id: number; name: string }
  default_currency: string
  edit_role: AdvanceEditRole
  can_edit: boolean
  can_decide: boolean
  items: RawAdvanceReceiptItem[]
}

interface RawReturnOption {
  node_id: number
  name: string
  asignee: string
}

export function hydrateAdvanceReceipts(raw: RawAdvanceReceiptsDetail, formData: RenditionFormData): AdvanceReceiptsDetail {
  return {
    requestId: raw.id,
    policyId: raw.policy.id,
    policyName: raw.policy.name,
    currency: raw.default_currency,
    editRole: raw.edit_role,
    canEdit: raw.can_edit,
    canDecide: raw.can_decide,
    receipts: raw.items.map((item) => ({
      invoiceId: item.id,
      renderedAmount: item.rendered_amount,
      decision: item.review_status ?? null,
    })),
    invoices: hydrateRenditionInvoices(raw.items, formData),
  }
}

export class HttpAdvanceRenditionRepository implements AdvanceRenditionRepository {
  constructor(private http: HttpClient) {}

  async getReceipts(id: number, formData: RenditionFormData): Promise<AdvanceReceiptsDetail> {
    const raw = await this.http.url(`purchases/requests/${id}/advance-receipts`).get<RawAdvanceReceiptsDetail>()
    return hydrateAdvanceReceipts(raw, formData)
  }

  // The backend replaces the receipt's whole line set, so lines travel without ids (see mapInvoice).
  async saveReceipt(id: number, invoice: RenditionInvoice, decimalsFor: (currency: string) => number): Promise<void> {
    await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({ action: 'EDIT_INVOICE', invoice: invoice.id, ...mapInvoice(invoice, decimalsFor) })
      .post<unknown>()
  }

  async reviewDocuments(id: number, input: ReviewDocumentsInput): Promise<ReviewDocumentsResult> {
    try {
      const res = await this.http
        .url(`purchases/requests/${id}/actions`)
        .body({
          action: 'REVIEW_DOCUMENTS',
          status: input.status,
          comments: input.comments,
          rejected_invoices: input.rejectedInvoiceIds,
          selected_previous: input.selectedPrevious,
        })
        .post<{ action: RequestActionOutcome }>()
      return { ok: true, outcome: res.action }
    } catch (e) {
      const options = ((e as AxiosError).response?.data as { extra?: { return_options?: RawReturnOption[] } } | undefined)
        ?.extra?.return_options
      if (!options?.length) throw e
      return {
        ok: false,
        returnOptions: options.map((o) => ({ nodeId: o.node_id, name: o.name, assignee: o.asignee })),
      }
    }
  }
}
